import React, { useState, useEffect, useRef } from "react";
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Plane,
  Plus,
  User,
  Mail,
  MapPin,
  Globe,
  DollarSign,
  Clock,
  Layers,
  Briefcase,
  Check,
  Loader2,
  FileText,
  Pencil,
  Receipt,
  Trash2,
  Users,
  X,
  Hash,
  Milestone,
  CalendarDays,
  AlertTriangle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  immigrationApi,
  immigrationProgramsApi,
  clientsApi,
  usersApi,
  invoicesApi,
  taxSettingsApi,
} from "../../services/api";
import {
  calculateTaxCents,
  getTaxLabel,
  getEffectiveRate,
  DEFAULT_TAX_CONFIG,
  TaxConfig,
  TaxBreakdown,
} from "../../utils/taxCalculation";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import { useGuidedTour } from "../../contexts/GuidedTourContext";
import QuickCreateClientModal from "../ui/QuickCreateClientModal";
import { useUserDataContext } from "../../context/UserDataContext";
import Toggle from "../ui/Toggle";

// ─── Disbursement Types & Presets ──────────────────────────────────────────────

interface DisbursementItem {
  id: string;
  name: string;
  amountCents: number;
  enabled: boolean;
  isCustom: boolean;
  taxable: boolean;
}

const CA_DISBURSEMENT_PRESETS: Omit<DisbursementItem, "enabled">[] = [
  { id: "ircc_processing", name: "IRCC Processing Fee", amountCents: 15500, isCustom: false, taxable: false },
  { id: "biometrics", name: "Biometrics Collection Fee", amountCents: 8500, isCustom: false, taxable: false },
  { id: "pr_landing", name: "Right of Permanent Residence Fee", amountCents: 51500, isCustom: false, taxable: false },
  { id: "sponsorship_fee", name: "Sponsorship Application Fee", amountCents: 7500, isCustom: false, taxable: false },
  { id: "passport_courier", name: "Passport / Document Courier", amountCents: 5000, isCustom: false, taxable: true },
  { id: "medical_exam", name: "Immigration Medical Exam", amountCents: 30000, isCustom: false, taxable: false },
  { id: "police_clearance", name: "Police Clearance / Background Check", amountCents: 7000, isCustom: false, taxable: false },
  { id: "translation", name: "Document Translation (Certified)", amountCents: 5000, isCustom: false, taxable: true },
];

const formatCAD = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

// ─── Milestone Type ───────────────────────────────────────────────────────────

interface MilestoneItem {
  key: string;
  label: string;
  status: string;
  dueAt: string | null;
}

// ─── Component Props ───────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
  onCreated: (id: string, meta?: { clientId: string; clientDisplayName: string; matterTitle: string }) => void;
}

const COUNTRIES = [
  { code: "CA", name: "Canada" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
];

const COUNTRY_COLORS: Record<string, string> = {
  CA: "bg-red-500",
  US: "bg-blue-500",
  GB: "bg-blue-700",
  AU: "bg-amber-500",
};

const CASE_TYPES = [
  { value: "temporary_resident", label: "Temporary Resident" },
  { value: "permanent_resident", label: "Permanent Resident" },
  { value: "citizenship", label: "Citizenship" },
  { value: "sponsorship", label: "Sponsorship" },
  { value: "employer_based", label: "Employer Based" },
  { value: "refugee", label: "Refugee" },
  { value: "appeal_review", label: "Appeal/Review" },
  { value: "other", label: "Other" },
];

const STEPS = [
  { num: 1, label: "Jurisdiction" },
  { num: 2, label: "Client" },
  { num: 3, label: "Billing" },
  { num: 4, label: "Review" },
];

const FEE_TYPE_OPTIONS = [
  { value: "flat", label: "Item Based", icon: DollarSign },
  { value: "hourly", label: "Hourly", icon: Clock },
  { value: "mixed", label: "Mixed", icon: Layers },
];

// ─── Helper: expand province/state abbreviations to full names ───────────────
const REGION_ABBREVS: Record<string, string> = {
  // Canada
  AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
  NL: "Newfoundland and Labrador", NS: "Nova Scotia", NT: "Northwest Territories",
  NU: "Nunavut", ON: "Ontario", PE: "Prince Edward Island", QC: "Quebec",
  SK: "Saskatchewan", YT: "Yukon",
  // US
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina",
  ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", DC: "District of Columbia",
};

function expandRegion(abbr: string): string {
  if (!abbr) return "";
  const upper = abbr.trim().toUpperCase();
  return REGION_ABBREVS[upper] || abbr;
}

// ─── Helper: get user initials ────────────────────────────────────────────────

const getUserInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0]?.toUpperCase() || "?";
};

// ─── Component ────────────────────────────────────────────────────────────────

const CreateImmigrationCaseModal: React.FC<Props> = ({ onClose, onCreated }) => {
  const { userData, firmData } = useUserDataContext();
  const { tour } = useGuidedTour();

  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState<{ title: string; id: string } | null>(null);
  const [showQuickCreateClient, setShowQuickCreateClient] = useState(false);
  const [showTourCloseWarning, setShowTourCloseWarning] = useState(false);
  const [programs, setPrograms] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showQuickCreateProgram, setShowQuickCreateProgram] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [quickProgram, setQuickProgram] = useState({ name: "", category: "WORK", caseType: "temporary_resident" });
  const [quickProgramSaving, setQuickProgramSaving] = useState(false);
  const directionRef = useRef(1);

  // ── Team Members (replaces single responsibleUserId) ──
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // ── Milestones ──
  const [milestones, setMilestones] = useState<MilestoneItem[]>([]);
  const [milestonesExpanded, setMilestonesExpanded] = useState(true);
  const [newMilestoneLabel, setNewMilestoneLabel] = useState("");
  const [editingMilestoneKey, setEditingMilestoneKey] = useState<string | null>(null);
  const [editingMilestoneLabel, setEditingMilestoneLabel] = useState("");

  // ── Document Checklist ──
  const [documentChecklist, setDocumentChecklist] = useState<{ key: string; label: string; required: boolean }[]>([]);
  const [checklistExpanded, setChecklistExpanded] = useState(true);
  const [newChecklistLabel, setNewChecklistLabel] = useState("");
  const [newChecklistRequired, setNewChecklistRequired] = useState(false);

  // ── Disbursement & Invoice State ──
  const [disbursements, setDisbursements] = useState<DisbursementItem[]>([]);
  const [customName, setCustomName] = useState("");
  const [customAmountDollars, setCustomAmountDollars] = useState("");
  const [showPresetPicker, setShowPresetPicker] = useState(false);
  const [legalFeeLabel, setLegalFeeLabel] = useState("Legal Fee");
  const [createInitialInvoice, setCreateInitialInvoice] = useState(false);
  const [invoiceMode, setInvoiceMode] = useState<"full" | "partial" | "disbursements_only">("full");
  const [partialAmountDollars, setPartialAmountDollars] = useState("");

  // ── Invoice Due Date ──
  const [dueDateOption, setDueDateOption] = useState<"1" | "7" | "30" | "custom">("30");
  const [customDueDate, setCustomDueDate] = useState("");
  const [sendInvoice, setSendInvoice] = useState(false);

  // ── Tax Config ──
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  // ── Saved Preferences Tracking ──
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [hasSavedPrefs, setHasSavedPrefs] = useState(false);

  // ── Disbursement inline editing ──
  const [editingDisbursementId, setEditingDisbursementId] = useState<string | null>(null);
  const [editingDisbursementName, setEditingDisbursementName] = useState("");

  const [form, setForm] = useState({
    country: firmData.jurisdiction?.country || "CA",
    region: expandRegion(firmData.jurisdiction?.region || ""),
    programKey: "",
    caseType: "temporary_resident",
    clientId: "",
    principalApplicantName: "",
    principalApplicantEmail: "",
    title: "",
    feeType: "flat" as "flat" | "hourly" | "mixed",
    flatFeeCents: 0,
  });

  const PROGRAM_CATEGORIES = [
    { value: "WORK", label: "Work" },
    { value: "PR", label: "Permanent Residence" },
    { value: "STUDY", label: "Study" },
    { value: "VISIT", label: "Visit" },
    { value: "SPONSOR", label: "Sponsorship" },
    { value: "CITIZENSHIP", label: "Citizenship" },
    { value: "REFUGEE", label: "Refugee" },
    { value: "OTHER", label: "Other" },
  ];

  // ── Load clients, users, and tax settings ──
  useEffect(() => {
    Promise.all([
      clientsApi.list({ limit: 200, excludeSample: true }),
      usersApi.list({ limit: 200 }),
    ]).then(([cRes, uRes]) => {
      setClients(cRes.data.data || []);
      setUsers(uRes.data.data || []);
    }).catch(() => {});

    taxSettingsApi.get().then((res) => {
      const s = res.data;
      if (s && s.taxMode) {
        setTaxConfig({
          taxMode: s.taxMode,
          hstRate: s.hstRate ?? 13,
          gstRate: s.gstRate ?? 5,
          pstRate: s.pstRate ?? 0,
        });
      }
    }).catch(() => {});
  }, []);

  // ── Auto-select current user as first team member ──
  useEffect(() => {
    if (userData._id && !selectedUserIds.includes(userData._id)) {
      setSelectedUserIds([userData._id]);
    }
  }, [userData._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load programs on country change ──
  const loadPrograms = async (country: string) => {
    try {
      const res = await immigrationProgramsApi.list({ country, active: "true", limit: 100 });
      const list = res.data.data || [];
      if (list.length === 0 && !seeding) {
        setSeeding(true);
        try {
          await immigrationProgramsApi.seed();
          const retry = await immigrationProgramsApi.list({ country, active: "true", limit: 100 });
          setPrograms(retry.data.data || []);
        } catch {
          setPrograms([]);
        } finally {
          setSeeding(false);
        }
      } else {
        setPrograms(list);
      }
    } catch {
      setPrograms([]);
    }
  };

  useEffect(() => {
    loadPrograms(form.country);
  }, [form.country]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-set case type from program ──
  useEffect(() => {
    if (form.programKey) {
      const prog = programs.find((p: any) => p.programKey === form.programKey);
      if (prog) {
        setForm((prev) => ({ ...prev, caseType: prog.caseType }));
      }
    }
  }, [form.programKey, programs]);

  // ── Load saved preferences OR program defaults when program changes ──
  useEffect(() => {
    if (!form.programKey) {
      setMilestones([]);
      setDocumentChecklist([]);
      setPrefsLoaded(false);
      setHasSavedPrefs(false);
      return;
    }

    let cancelled = false;

    const loadPreferences = async () => {
      setPrefsLoaded(false);
      setHasSavedPrefs(false);

      try {
        const res = await immigrationApi.getPreferences(form.programKey);
        const prefs = res.data;

        if (cancelled) return;

        if (prefs && (prefs.milestones || prefs.disbursements || prefs.invoiceSettings || prefs.documentChecklist)) {
          setHasSavedPrefs(true);

          // Apply saved milestones
          if (prefs.milestones?.length > 0) {
            setMilestones(
              prefs.milestones.map((m: any) => ({
                key: m.key,
                label: m.label,
                status: "not_started",
                dueAt: null,
              }))
            );
          } else {
            // Fall back to program templates
            const prog = programs.find((p: any) => p.programKey === form.programKey);
            if (prog?.milestoneTemplates?.length > 0) {
              setMilestones(
                prog.milestoneTemplates.map((t: any) => ({
                  key: t.key, label: t.label, status: "not_started", dueAt: null,
                }))
              );
            } else {
              setMilestones([]);
            }
          }

          // Apply saved document checklist
          if (prefs.documentChecklist?.length > 0) {
            setDocumentChecklist(prefs.documentChecklist.map((d: any) => ({
              key: d.key, label: d.label, required: d.required || false,
            })));
          } else {
            // Fall back to program document checklist templates
            const prog = programs.find((p: any) => p.programKey === form.programKey);
            if (prog?.documentChecklistTemplates?.length > 0) {
              setDocumentChecklist(prog.documentChecklistTemplates.map((t: any) => ({
                key: t.key, label: t.label, required: t.required || false,
              })));
            } else {
              setDocumentChecklist([]);
            }
          }

          // Apply saved disbursements (only enabled ones in new add-based UI)
          if (prefs.disbursements?.length > 0) {
            setDisbursements(prefs.disbursements.filter((d: DisbursementItem) => d.enabled !== false));
          }

          // Apply saved invoice settings
          if (prefs.invoiceSettings) {
            const inv = prefs.invoiceSettings;
            if (inv.feeType) setForm((prev) => ({ ...prev, feeType: inv.feeType, flatFeeCents: inv.flatFeeCents || 0 }));
            if (inv.legalFeeLabel) setLegalFeeLabel(inv.legalFeeLabel);
            if (inv.createInitialInvoice !== undefined) setCreateInitialInvoice(inv.createInitialInvoice);
            if (inv.invoiceMode) setInvoiceMode(inv.invoiceMode);
            if (inv.dueDateOption) setDueDateOption(inv.dueDateOption);
          }

          setPrefsLoaded(true);
          return;
        }
      } catch {
        // No saved preferences  -  fall through to program defaults
      }

      if (cancelled) return;

      // Fall back to program templates
      const prog = programs.find((p: any) => p.programKey === form.programKey);
      if (prog?.milestoneTemplates?.length > 0) {
        setMilestones(
          prog.milestoneTemplates.map((t: any) => ({
            key: t.key, label: t.label, status: "not_started", dueAt: null,
          }))
        );
      } else {
        setMilestones([]);
      }
      if (prog?.documentChecklistTemplates?.length > 0) {
        setDocumentChecklist(
          prog.documentChecklistTemplates.map((t: any) => ({
            key: t.key, label: t.label, required: t.required || false,
          }))
        );
      } else {
        setDocumentChecklist([]);
      }
      setPrefsLoaded(true);
    };

    loadPreferences();
    return () => { cancelled = true; };
  }, [form.programKey, programs]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-populate principal applicant from selected client ──
  const lastAutoFilledClientId = useRef("");

  useEffect(() => {
    if (!form.clientId || form.clientId === lastAutoFilledClientId.current) return;
    const client = clients.find((c: any) => c._id === form.clientId);
    if (!client) return;

    lastAutoFilledClientId.current = form.clientId;
    const primaryEmail =
      client.emails?.find((e: any) => e.label === "main" || e.label === "primary")?.email ||
      client.emails?.[0]?.email ||
      "";

    setForm((prev) => ({
      ...prev,
      principalApplicantName: client.displayName || "",
      principalApplicantEmail: primaryEmail,
    }));
  }, [form.clientId, clients]);

  // ── Auto-generate title ──
  useEffect(() => {
    const client = clients.find((c: any) => c._id === form.clientId);
    const prog = programs.find((p: any) => p.programKey === form.programKey);
    if (client && prog) {
      setForm((prev) => ({ ...prev, title: `${prog.name} - ${client.displayName}` }));
    }
  }, [form.clientId, form.programKey, clients, programs]);

  // ── Duplicate case warning ──
  useEffect(() => {
    if (!form.clientId || !form.programKey) {
      setDuplicateWarning(null);
      return;
    }
    let cancelled = false;
    immigrationApi.listCases({ clientId: form.clientId, programKey: form.programKey, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const existing = res.data?.data?.[0];
        setDuplicateWarning(existing ? { title: existing.title, id: existing._id } : null);
      })
      .catch(() => { if (!cancelled) setDuplicateWarning(null); });
    return () => { cancelled = true; };
  }, [form.clientId, form.programKey]);

  // ── Disbursement Helpers ──
  const updateDisbursementAmount = (id: string, dollars: string) => {
    const cents = Math.round(parseFloat(dollars || "0") * 100);
    setDisbursements((prev) => prev.map((d) => (d.id === id ? { ...d, amountCents: cents } : d)));
  };

  const updateDisbursementName = (id: string, name: string) => {
    setDisbursements((prev) => prev.map((d) => (d.id === id ? { ...d, name } : d)));
  };

  const addCustomDisbursement = () => {
    if (!customName.trim()) return;
    const cents = Math.round(parseFloat(customAmountDollars || "0") * 100);
    setDisbursements((prev) => [
      ...prev,
      {
        id: `custom_${Date.now()}`,
        name: customName.trim(),
        amountCents: cents,
        enabled: true,
        isCustom: true,
        taxable: true,
      },
    ]);
    setCustomName("");
    setCustomAmountDollars("");
  };

  const removeDisbursement = (id: string) => {
    setDisbursements((prev) => prev.filter((d) => d.id !== id));
  };

  const addPresetCharge = (preset: Omit<DisbursementItem, "enabled">) => {
    if (disbursements.some((d) => d.id === preset.id)) return;
    setDisbursements((prev) => [...prev, { ...preset, enabled: true }]);
  };

  // ── Milestone Helpers ──
  const addMilestone = () => {
    if (!newMilestoneLabel.trim()) return;
    const key = newMilestoneLabel
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/(^_|_$)/g, "");
    setMilestones((prev) => [
      ...prev,
      { key: `custom_${key}_${Date.now()}`, label: newMilestoneLabel.trim(), status: "not_started", dueAt: null },
    ]);
    setNewMilestoneLabel("");
  };

  const removeMilestone = (key: string) => {
    setMilestones((prev) => prev.filter((m) => m.key !== key));
  };

  const startEditMilestone = (key: string, label: string) => {
    setEditingMilestoneKey(key);
    setEditingMilestoneLabel(label);
  };

  const saveMilestoneEdit = () => {
    if (editingMilestoneKey && editingMilestoneLabel.trim()) {
      setMilestones((prev) =>
        prev.map((m) =>
          m.key === editingMilestoneKey ? { ...m, label: editingMilestoneLabel.trim() } : m
        )
      );
    }
    setEditingMilestoneKey(null);
    setEditingMilestoneLabel("");
  };

  // ── Team Member Helpers ──
  const addTeamMember = (userId: string) => {
    if (userId && !selectedUserIds.includes(userId)) {
      setSelectedUserIds((prev) => [...prev, userId]);
    }
  };

  const removeTeamMember = (userId: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  };

  // ── Computed Totals ──
  const enabledDisbursements = disbursements.filter((d) => d.enabled);
  const totalDisbursementsCents = enabledDisbursements.reduce((sum, d) => sum + d.amountCents, 0);
  const legalFeeCents = form.feeType !== "hourly" ? form.flatFeeCents : 0;
  const subtotalCents = legalFeeCents + totalDisbursementsCents;

  // ── Tax Calculations ──
  const taxableCents = legalFeeCents + enabledDisbursements.filter((d) => d.taxable).reduce((sum, d) => sum + d.amountCents, 0);
  const taxBreakdown: TaxBreakdown = calculateTaxCents(taxableCents, taxConfig);
  const grandTotalCents = subtotalCents + taxBreakdown.totalTaxCents;
  const taxLabel = getTaxLabel(taxConfig);

  const partialCents = Math.round(parseFloat(partialAmountDollars || "0") * 100);
  const invoiceAmountCents = !createInitialInvoice
    ? 0
    : invoiceMode === "full"
    ? grandTotalCents
    : invoiceMode === "disbursements_only"
    ? totalDisbursementsCents + calculateTaxCents(enabledDisbursements.filter((d) => d.taxable).reduce((sum, d) => sum + d.amountCents, 0), taxConfig).totalTaxCents
    : Math.min(partialCents, grandTotalCents);

  // ── Compute due date ──
  const computeDueDate = (): Date => {
    const d = new Date();
    if (dueDateOption === "custom" && customDueDate) {
      d.setTime(new Date(customDueDate).getTime());
    } else {
      d.setDate(d.getDate() + parseInt(dueDateOption));
    }
    return d;
  };

  const formatDueDate = (): string => {
    const d = computeDueDate();
    return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  };

  // ── Handle Create ──
  const handleCreate = async () => {
    setCreating(true);
    setError("");
    try {
      const payload: Record<string, any> = {
        clientId: form.clientId,
        title: form.title,
        jurisdiction: { country: form.country, region: form.region || null },
        caseType: form.caseType,
        program: { programKey: form.programKey, programVersion: 1 },
        assigned: {
          responsibleUserId: selectedUserIds[0],
          assignedUserIds: selectedUserIds.slice(1),
        },
        billing: {
          feeType: form.feeType,
          flatFeeCents: form.feeType === "flat" ? form.flatFeeCents : 0,
        },
        parties: [],
        milestones: milestones.length > 0 ? milestones : undefined,
        documentChecklist: documentChecklist.length > 0 ? documentChecklist : undefined,
      };

      if (form.principalApplicantName) {
        payload.parties.push({
          role: "principal_applicant",
          clientId: form.clientId,
          displayName: form.principalApplicantName,
          email: form.principalApplicantEmail || null,
        });
      }

      const res = await immigrationApi.createCase(payload);
      const caseId = res.data._id;
      const createdMatterId = res.data.matterId?._id || res.data.matterId || caseId;

      // Create initial invoice if toggled on
      if (createInitialInvoice && invoiceAmountCents > 0) {
        try {
          const lineItems: any[] = [];

          if (invoiceMode === "full") {
            if (legalFeeCents > 0) {
              const feeTax = calculateTaxCents(legalFeeCents, taxConfig);
              lineItems.push({
                kind: "fee",
                description: `${legalFeeLabel}  -  ${form.title}`,
                quantity: 1,
                unitPriceCents: legalFeeCents,
                amountCents: legalFeeCents,
                taxable: true,
                taxAmountCents: feeTax.totalTaxCents,
              });
            }
            enabledDisbursements.forEach((d) => {
              const dTax = d.taxable ? calculateTaxCents(d.amountCents, taxConfig) : { totalTaxCents: 0 };
              lineItems.push({
                kind: "disbursement",
                description: d.name,
                quantity: 1,
                unitPriceCents: d.amountCents,
                amountCents: d.amountCents,
                taxable: d.taxable,
                taxAmountCents: dTax.totalTaxCents,
              });
            });
          } else if (invoiceMode === "disbursements_only") {
            enabledDisbursements.forEach((d) => {
              const dTax = d.taxable ? calculateTaxCents(d.amountCents, taxConfig) : { totalTaxCents: 0 };
              lineItems.push({
                kind: "disbursement",
                description: d.name,
                quantity: 1,
                unitPriceCents: d.amountCents,
                amountCents: d.amountCents,
                taxable: d.taxable,
                taxAmountCents: dTax.totalTaxCents,
              });
            });
          } else if (invoiceMode === "partial" && partialCents > 0) {
            const cappedCents = Math.min(partialCents, grandTotalCents);
            const retainerTax = calculateTaxCents(cappedCents, taxConfig);
            lineItems.push({
              kind: "fee",
              description: `Retainer  -  ${form.title}`,
              quantity: 1,
              unitPriceCents: cappedCents,
              amountCents: cappedCents,
              taxable: true,
              taxAmountCents: retainerTax.totalTaxCents,
            });
          }

          if (lineItems.length > 0) {
            const dueDate = computeDueDate();
            // Guard against Invalid Date
            const dueDateStr = isNaN(dueDate.getTime())
              ? new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)
              : dueDate.toISOString().slice(0, 10);

            // Extract matterId string for linking invoice to the matter
            const invoiceMatterId = typeof createdMatterId === "string" ? createdMatterId : String(createdMatterId);

            const invoiceRes = await invoicesApi.create({
              clientId: form.clientId,
              matterId: invoiceMatterId,
              lineItems,
              dueAt: dueDateStr,
              internalNotes: `Auto-generated from immigration case: ${form.title}`,
            });

            if (sendInvoice && invoiceRes?.data?._id) {
              try {
                await invoicesApi.send(invoiceRes.data._id);
              } catch (sendErr) {
                console.error("Invoice created but failed to send:", sendErr);
              }
            }
          }
        } catch (invoiceErr) {
          console.error("Failed to create initial invoice:", invoiceErr);
        }
      }

      // Save preferences for this program (fire-and-forget)
      try {
        await immigrationApi.savePreferences(form.programKey, {
          milestones: milestones.map((m) => ({ key: m.key, label: m.label })),
          documentChecklist: documentChecklist.map((d) => ({ key: d.key, label: d.label, required: d.required })),
          disbursements: disbursements.map((d) => ({
            id: d.id,
            name: d.name,
            amountCents: d.amountCents,
            enabled: d.enabled,
            isCustom: d.isCustom,
            taxable: d.taxable,
          })),
          invoiceSettings: {
            feeType: form.feeType,
            flatFeeCents: form.flatFeeCents,
            legalFeeLabel,
            createInitialInvoice,
            invoiceMode,
            dueDateOption,
          },
        });
      } catch {
        // Non-critical  -  don't block case creation
      }

      const client = clients.find((c: any) => c._id === form.clientId);
      onCreated(createdMatterId, {
        clientId: form.clientId,
        clientDisplayName: client?.displayName || "",
        matterTitle: form.title,
      });
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          (err.response?.data?.details ? JSON.stringify(err.response.data.details) : "Failed to create immigration matter")
      );
    } finally {
      setCreating(false);
    }
  };

  // ── Can Proceed ──
  const canProceed = () => {
    switch (step) {
      case 1:
        return form.country && form.programKey;
      case 2:
        return form.clientId && selectedUserIds.length > 0;
      case 3:
        return true; // billing is optional
      case 4:
        return form.title;
      default:
        return false;
    }
  };

  const goToStep = (target: number) => {
    directionRef.current = target > step ? 1 : -1;
    setStep(target);
  };

  const goNext = () => {
    directionRef.current = 1;
    setStep(step + 1);
  };

  const goBack = () => {
    directionRef.current = -1;
    setStep(step - 1);
  };

  const countryName = COUNTRIES.find((c) => c.code === form.country)?.name || "";
  const selectedClient = clients.find((c: any) => c._id === form.clientId);
  const selectedProgram = programs.find((p: any) => p.programKey === form.programKey);

  const slideVariants = {
    enter: (dir: number) => ({ opacity: 0, x: dir * 20 }),
    center: { opacity: 1, x: 0 },
    exit: (dir: number) => ({ opacity: 0, x: dir * -20 }),
  };

  const handleClose = tour.isActive ? () => setShowTourCloseWarning(true) : onClose;

  return (
    <>
      {/* Tour close warning popup */}
      {showTourCloseWarning && (
        <div className="fixed inset-0 z-[10010] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowTourCloseWarning(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-6 p-6 text-center">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Almost there!</h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              Just finish creating this matter to complete the walkthrough  -  it only takes a moment. You can always delete it later.
            </p>
            <button
              type="button"
              onClick={() => setShowTourCloseWarning(false)}
              className="mt-5 w-full px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              Continue creating matter
            </button>
          </div>
        </div>
      )}

      <PremiumModal
        open={true}
        onClose={handleClose}
        title="New Immigration Matter"
        subtitle={`Step ${step} of 4`}
        icon={Plane}
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <button
              type="button"
              onClick={step === 1 ? handleClose : goBack}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              {step === 1 ? "Cancel" : "Back"}
            </button>
            {step < 4 ? (
              <button
                type="button"
                disabled={!canProceed()}
                onClick={goNext}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                disabled={creating || !canProceed()}
                onClick={handleCreate}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                {creating ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </span>
                ) : (
                  "Create Immigration Matter"
                )}
              </button>
            )}
          </div>
        }
      >
        {/* Tour hint */}
        {tour.isActive && (
          <div className="mb-4 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 leading-relaxed">
            This is just a walkthrough  -  you can delete this matter later if you'd rather create a real one after.
          </div>
        )}

        {/* Step Progress Bar with Labels */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all ${
                      s.num < step
                        ? "bg-brand-accent text-white"
                        : s.num === step
                        ? "bg-brand-accent text-white"
                        : "bg-slate-200 text-slate-400"
                    }`}
                  >
                    {s.num < step ? <Check className="h-4 w-4" /> : s.num}
                  </div>
                  <span
                    className={`text-xs transition-colors ${
                      s.num < step
                        ? "text-brand-accent font-medium"
                        : s.num === step
                        ? "text-brand font-semibold"
                        : "text-slate-400 font-medium"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-px mx-2 mb-5 transition-colors ${
                      s.num < step ? "bg-brand-accent" : "bg-slate-200"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content with Animated Transitions */}
        <AnimatePresence mode="wait" custom={directionRef.current}>
          <motion.div
            key={step}
            custom={directionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <div className="space-y-4">
              {/* ═══════════════════════════════════════════════════════════════
                  Step 1: Jurisdiction & Program (+ Milestones)
                  ═══════════════════════════════════════════════════════════════ */}
              {step === 1 && (() => {
                const filteredCountries = firmData.jurisdiction?.country
                  ? COUNTRIES.filter((c) => c.code === firmData.jurisdiction?.country)
                  : COUNTRIES;
                const singleCountry = filteredCountries.length === 1;

                return (
                <>
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {singleCountry ? "Jurisdiction" : "Country"}
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  {singleCountry ? (
                    /* Single country  -  country card + region input side by side, 50/50 */
                    <div>
                      <p className="text-[11px] text-slate-400 mb-2">Other countries can be added in settings</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 px-4 rounded-xl border-2 border-brand-accent bg-brand-light text-brand h-[46px]">
                          <div
                            className={`w-8 h-8 rounded-full ${COUNTRY_COLORS[filteredCountries[0].code]} flex items-center justify-center flex-shrink-0`}
                          >
                            <span className="text-xs font-bold text-white">{filteredCountries[0].code}</span>
                          </div>
                          <span className="text-sm font-medium">{filteredCountries[0].name}</span>
                        </div>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <input
                            type="text"
                            value={form.region}
                            onChange={(e) => setForm({ ...form, region: e.target.value })}
                            placeholder="Province / State (e.g. Ontario)"
                            className="w-full rounded-xl border border-slate-300 pl-10 pr-4 h-[46px] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Multiple countries  -  original grid layout */
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        {filteredCountries.map((c) => (
                          <button
                            key={c.code}
                            type="button"
                            onClick={() => setForm({ ...form, country: c.code, programKey: "" })}
                            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                              form.country === c.code
                                ? "border-brand-accent bg-brand-light text-brand"
                                : "border-slate-200 hover:border-slate-300 text-slate-600"
                            }`}
                          >
                            <div
                              className={`w-9 h-9 rounded-full ${COUNTRY_COLORS[c.code]} flex items-center justify-center`}
                            >
                              <span className="text-xs font-bold text-white">{c.code}</span>
                            </div>
                            <span className="text-sm font-medium">{c.name}</span>
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-3 pt-2">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Region & Program
                        </span>
                        <div className="flex-1 h-px bg-slate-100" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Region (Province/State)
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <input
                            type="text"
                            value={form.region}
                            onChange={(e) => setForm({ ...form, region: e.target.value })}
                            placeholder="e.g. ON, BC, NY"
                            className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {singleCountry && (
                    <div className="flex items-center gap-3 pt-2">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Program</span>
                      <div className="flex-1 h-px bg-slate-100" />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Program *</label>
                    {seeding ? (
                      <div className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-400 flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading default programs...
                      </div>
                    ) : (
                      <SearchableSelect
                        options={programs.map((p: any) => ({
                          value: p.programKey,
                          label: `${p.name} (${p.category})`,
                        }))}
                        value={form.programKey || null}
                        onChange={(val) => setForm({ ...form, programKey: val || "" })}
                        placeholder="Select a program"
                        required
                        addNew={{
                          label: "Create custom program",
                          onAdd: () => setShowQuickCreateProgram(true),
                        }}
                      />
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <p className="text-xs text-slate-400">Programs available for {countryName}</p>
                      {form.caseType && form.programKey && (
                        <span className="px-2.5 py-1 rounded-full bg-brand-light text-brand text-xs font-medium capitalize">
                          {CASE_TYPES.find((ct) => ct.value === form.caseType)?.label || form.caseType}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Milestones Section ── */}
                  {form.programKey && (
                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={() => setMilestonesExpanded(!milestonesExpanded)}
                        className="flex items-center gap-3 w-full"
                      >
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Program Milestones
                        </span>
                        {milestones.length > 0 && (
                          <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-brand-light text-brand">
                            {milestones.length}
                          </span>
                        )}
                        <div className="flex-1 h-px bg-slate-100" />
                        {milestonesExpanded ? (
                          <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                        )}
                      </button>

                      {milestonesExpanded && (
                        <div className="mt-3 space-y-1.5">
                          {milestones.length === 0 ? (
                            <p className="text-xs text-slate-400 py-2">
                              No milestones for this program
                            </p>
                          ) : (
                            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                              {milestones.map((m, idx) => (
                                <div
                                  key={m.key}
                                  className="flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-slate-50 transition-colors group"
                                >
                                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-brand-light text-brand text-[10px] font-bold flex-shrink-0">
                                    {idx + 1}
                                  </div>
                                  {editingMilestoneKey === m.key ? (
                                    <input
                                      type="text"
                                      value={editingMilestoneLabel}
                                      onChange={(e) => setEditingMilestoneLabel(e.target.value)}
                                      onBlur={saveMilestoneEdit}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") saveMilestoneEdit();
                                        if (e.key === "Escape") {
                                          setEditingMilestoneKey(null);
                                          setEditingMilestoneLabel("");
                                        }
                                      }}
                                      autoFocus
                                      className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                                    />
                                  ) : (
                                    <span
                                      className="flex-1 text-sm text-slate-700 cursor-pointer hover:text-brand transition-colors"
                                      onClick={() => startEditMilestone(m.key, m.label)}
                                      title="Click to edit"
                                    >
                                      {m.label}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => removeMilestone(m.key)}
                                    className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add milestone row */}
                          <div className="flex items-center gap-2 pt-1">
                            <input
                              type="text"
                              value={newMilestoneLabel}
                              onChange={(e) => setNewMilestoneLabel(e.target.value)}
                              placeholder="Add milestone..."
                              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                              onKeyDown={(e) => e.key === "Enter" && addMilestone()}
                            />
                            <button
                              type="button"
                              onClick={addMilestone}
                              disabled={!newMilestoneLabel.trim()}
                              className="p-2 rounded-lg bg-brand-light text-brand hover:bg-brand-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>

                          {/* Preference memory hint */}
                          <p className="text-[11px] text-slate-400 italic flex items-center gap-1.5 pt-1">
                            <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Z"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                            {hasSavedPrefs
                              ? "Loaded your saved milestones for this program. Changes will be remembered."
                              : "Your milestone preferences for this program will be remembered for next time."}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Document Checklist Section ── */}
                  {form.programKey && (
                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={() => setChecklistExpanded(!checklistExpanded)}
                        className="flex items-center gap-3 w-full"
                      >
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Document Checklist
                        </span>
                        {documentChecklist.length > 0 && (
                          <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-brand-light text-brand">
                            {documentChecklist.length}
                          </span>
                        )}
                        <div className="flex-1 h-px bg-slate-100" />
                        {checklistExpanded ? (
                          <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                        )}
                      </button>

                      {checklistExpanded && (
                        <div className="mt-3 space-y-1.5">
                          {documentChecklist.length === 0 ? (
                            <p className="text-xs text-slate-400 py-2">
                              No document checklist items for this program
                            </p>
                          ) : (
                            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                              {documentChecklist.map((item, idx) => (
                                <div
                                  key={item.key}
                                  className="flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-slate-50 transition-colors group"
                                >
                                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-brand-light text-brand text-[10px] font-bold flex-shrink-0">
                                    {idx + 1}
                                  </div>
                                  <span className="flex-1 text-sm text-slate-700">{item.label}</span>
                                  {item.required && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-50 text-red-600">Req</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => setDocumentChecklist((prev) => prev.map((d) => d.key === item.key ? { ...d, required: !d.required } : d))}
                                    className="p-1 rounded text-slate-300 hover:text-amber-500 hover:bg-amber-50 transition-colors opacity-0 group-hover:opacity-100"
                                    title="Toggle required"
                                  >
                                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setDocumentChecklist((prev) => prev.filter((d) => d.key !== item.key))}
                                    className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add checklist item row */}
                          <div className="flex items-center gap-2 pt-1">
                            <input
                              type="text"
                              value={newChecklistLabel}
                              onChange={(e) => setNewChecklistLabel(e.target.value)}
                              placeholder="Add document..."
                              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && newChecklistLabel.trim()) {
                                  const key = newChecklistLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
                                  setDocumentChecklist((prev) => [...prev, { key: `doc_${key}_${Date.now()}`, label: newChecklistLabel.trim(), required: newChecklistRequired }]);
                                  setNewChecklistLabel("");
                                  setNewChecklistRequired(false);
                                }
                              }}
                            />
                            <label className="flex items-center gap-1 text-[11px] text-slate-500 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newChecklistRequired}
                                onChange={(e) => setNewChecklistRequired(e.target.checked)}
                                className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600"
                              />
                              Req
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                if (!newChecklistLabel.trim()) return;
                                const key = newChecklistLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
                                setDocumentChecklist((prev) => [...prev, { key: `doc_${key}_${Date.now()}`, label: newChecklistLabel.trim(), required: newChecklistRequired }]);
                                setNewChecklistLabel("");
                                setNewChecklistRequired(false);
                              }}
                              disabled={!newChecklistLabel.trim()}
                              className="p-2 rounded-lg bg-brand-light text-brand hover:bg-brand-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>

                          <p className="text-[11px] text-slate-400 italic flex items-center gap-1.5 pt-1">
                            <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Z"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                            {hasSavedPrefs
                              ? "Loaded your saved checklist for this program. Changes will be remembered."
                              : "Your checklist will be remembered for next time."}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </>
                );
              })()}

              {/* ═══════════════════════════════════════════════════════════════
                  Step 2: Client & Team
                  ═══════════════════════════════════════════════════════════════ */}
              {step === 2 && (
                <>
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Client</span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  {tour.isActive && (
                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
                      <span className="text-sm text-amber-800">
                        Select an existing client or create a new one  -  use a real client or a test name for this walkthrough.
                      </span>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
                    <SearchableSelect
                      options={clients.map((c: any) => ({ value: c._id, label: c.displayName }))}
                      value={form.clientId || null}
                      onChange={(val) => setForm({ ...form, clientId: val || "" })}
                      placeholder="Select a client"
                      required
                      addNew={{
                        label: "Add new client",
                        onAdd: () => setShowQuickCreateClient(true),
                      }}
                    />
                    {selectedClient && (
                      <div className="flex items-center gap-2 mt-1.5 px-3 py-1.5 bg-brand-light rounded-lg text-xs font-medium text-brand">
                        <User className="h-3 w-3" />
                        {selectedClient.displayName}
                      </div>
                    )}
                    {duplicateWarning && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            A case already exists for this client with this program
                          </p>
                          <p className="text-sm text-amber-700 mt-0.5">
                            "{duplicateWarning.title}"  -  Are you sure you want to create another?
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Principal Applicant
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={form.principalApplicantName}
                        onChange={(e) => setForm({ ...form, principalApplicantName: e.target.value })}
                        placeholder="Full name"
                        className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="email"
                        value={form.principalApplicantEmail}
                        onChange={(e) => setForm({ ...form, principalApplicantEmail: e.target.value })}
                        placeholder="email@example.com"
                        className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      Dependents and additional parties can be added after creation
                    </p>
                  </div>

                  {/* ── Team Members ── */}
                  <div className="flex items-center gap-3 pt-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Team</span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  {/* Selected team members as chips */}
                  {selectedUserIds.length > 0 && (
                    <div className="flex flex-wrap gap-3">
                      {selectedUserIds.map((uid, idx) => {
                        const u = users.find((usr: any) => usr._id === uid);
                        if (!u) return null;
                        return (
                          <div
                            key={uid}
                            className="flex flex-col items-center gap-1.5 relative group"
                          >
                            <div className="relative">
                              <div className="w-11 h-11 rounded-full bg-brand-light text-brand flex items-center justify-center text-sm font-bold">
                                {getUserInitials(u.name)}
                              </div>
                              {idx === 0 && (
                                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[9px] font-bold bg-brand-accent text-white whitespace-nowrap">
                                  Lead
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => removeTeamMember(uid)}
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </div>
                            <span className="text-[11px] text-slate-600 font-medium max-w-[70px] truncate text-center">
                              {u.name?.split(" ")[0] || "User"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add team member dropdown */}
                  <SearchableSelect
                    options={users
                      .filter((u: any) => !selectedUserIds.includes(u._id))
                      .map((u: any) => ({ value: u._id, label: u.name }))}
                    value={null}
                    onChange={(val) => {
                      if (val) addTeamMember(val);
                    }}
                    placeholder="Add team member..."
                  />
                </>
              )}

              {/* ═══════════════════════════════════════════════════════════════
                  Step 3: Billing (Fee + Charges + Invoice)
                  ═══════════════════════════════════════════════════════════════ */}
              {step === 3 && (
                <>
                  {/* ── Fee Structure Selector ── */}
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Fee Structure
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    {FEE_TYPE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm({ ...form, feeType: opt.value as any })}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          form.feeType === opt.value
                            ? "border-brand-accent bg-brand-light text-brand"
                            : "border-slate-200 hover:border-slate-300 text-slate-600"
                        }`}
                      >
                        <opt.icon className="h-5 w-5" />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* ── Charges (unified line items) ── */}
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Charges
                    </span>
                    {(enabledDisbursements.length > 0 || (form.feeType !== "hourly" && form.flatFeeCents > 0)) && (
                      <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-brand-light text-brand">
                        {enabledDisbursements.length + (form.feeType !== "hourly" && form.flatFeeCents > 0 ? 1 : 0)}
                      </span>
                    )}
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  {/* Custom add input at top */}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      placeholder="Add charge item..."
                      className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                      onKeyDown={(e) => e.key === "Enter" && addCustomDisbursement()}
                    />
                    <div className="relative w-28">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <input
                        type="number"
                        value={customAmountDollars}
                        onChange={(e) => setCustomAmountDollars(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        className="w-full rounded-xl border border-slate-200 pl-7 pr-2 py-2.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                        onKeyDown={(e) => e.key === "Enter" && addCustomDisbursement()}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addCustomDisbursement}
                      disabled={!customName.trim()}
                      className="p-2.5 rounded-xl bg-brand-accent text-white hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Charges list */}
                  {(form.feeType !== "hourly" || enabledDisbursements.length > 0) && (
                    <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                      {/* Legal fee row (for Item Based / Mixed) */}
                      {form.feeType !== "hourly" && (
                        <div className="flex items-center gap-3 px-3 py-2.5 bg-emerald-50/60">
                          <DollarSign className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                          {editingDisbursementId === "__legal_fee__" ? (
                            <input
                              type="text"
                              value={legalFeeLabel}
                              onChange={(e) => setLegalFeeLabel(e.target.value)}
                              onBlur={() => setEditingDisbursementId(null)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === "Escape") setEditingDisbursementId(null);
                              }}
                              autoFocus
                              className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                            />
                          ) : (
                            <span
                              className="flex-1 text-sm font-medium text-slate-800 cursor-text"
                              onClick={() => setEditingDisbursementId("__legal_fee__")}
                              title="Click to edit name"
                            >
                              {legalFeeLabel}
                              {form.feeType === "mixed" && (
                                <span className="text-xs text-slate-400 ml-1.5">(flat portion)</span>
                              )}
                            </span>
                          )}
                          <div className="relative w-28">
                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                            <input
                              type="number"
                              value={form.flatFeeCents / 100 || ""}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  flatFeeCents: Math.round(parseFloat(e.target.value || "0") * 100),
                                })
                              }
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              className="w-full rounded-lg border border-slate-200 pl-7 pr-2 py-1.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                            />
                          </div>
                        </div>
                      )}

                      {/* Added disbursement / charge items */}
                      {enabledDisbursements.map((d) => (
                        <div
                          key={d.id}
                          className="flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-slate-50 transition-colors group"
                        >
                          <Receipt className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          {editingDisbursementId === d.id ? (
                            <input
                              type="text"
                              value={editingDisbursementName}
                              onChange={(e) => setEditingDisbursementName(e.target.value)}
                              onBlur={() => {
                                if (editingDisbursementName.trim()) {
                                  updateDisbursementName(d.id, editingDisbursementName.trim());
                                }
                                setEditingDisbursementId(null);
                                setEditingDisbursementName("");
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  if (editingDisbursementName.trim()) {
                                    updateDisbursementName(d.id, editingDisbursementName.trim());
                                  }
                                  setEditingDisbursementId(null);
                                  setEditingDisbursementName("");
                                }
                                if (e.key === "Escape") {
                                  setEditingDisbursementId(null);
                                  setEditingDisbursementName("");
                                }
                              }}
                              autoFocus
                              className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                            />
                          ) : (
                            <span
                              className="flex-1 text-sm text-slate-700 cursor-text"
                              onClick={() => {
                                setEditingDisbursementId(d.id);
                                setEditingDisbursementName(d.name);
                              }}
                              title="Click to edit name"
                            >
                              {d.name}
                            </span>
                          )}
                          <div className="relative w-28">
                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                            <input
                              type="number"
                              value={d.amountCents / 100 || ""}
                              onChange={(e) => updateDisbursementAmount(d.id, e.target.value)}
                              step="0.01"
                              min="0"
                              className="w-full rounded-lg border border-slate-200 pl-7 pr-2 py-1.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDisbursement(d.id)}
                            className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Browse common charges (preset picker) */}
                  {form.country === "CA" && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowPresetPicker(!showPresetPicker)}
                        className="flex items-center gap-2 text-xs font-medium text-brand-accent hover:text-brand transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Common Charge
                        {showPresetPicker ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                      </button>

                      {showPresetPicker && (
                        <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden max-h-48 overflow-y-auto">
                          {CA_DISBURSEMENT_PRESETS.filter(
                            (p) => !disbursements.some((d) => d.id === p.id)
                          ).length > 0 ? (
                            CA_DISBURSEMENT_PRESETS.filter(
                              (p) => !disbursements.some((d) => d.id === p.id)
                            ).map((preset) => (
                              <button
                                key={preset.id}
                                type="button"
                                onClick={() => {
                                  addPresetCharge(preset);
                                }}
                                className="flex items-center gap-3 px-3 py-2 w-full text-left hover:bg-white transition-colors border-b border-slate-100 last:border-b-0"
                              >
                                <Plus className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                                <span className="flex-1 text-sm text-slate-600">{preset.name}</span>
                                <span className="text-xs text-slate-400">{formatCAD(preset.amountCents)}</span>
                              </button>
                            ))
                          ) : (
                            <p className="text-xs text-slate-400 px-3 py-2.5 text-center">
                              All common charges have been added.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Charges summary with tax breakdown */}
                  {subtotalCents > 0 && (
                    <div className="space-y-1 px-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">
                          {enabledDisbursements.length + (form.feeType !== "hourly" && form.flatFeeCents > 0 ? 1 : 0)} item
                          {enabledDisbursements.length + (form.feeType !== "hourly" && form.flatFeeCents > 0 ? 1 : 0) !== 1 ? "s" : ""}
                        </span>
                        <span className="text-xs text-slate-500">{formatCAD(subtotalCents)}</span>
                      </div>
                      {taxBreakdown.totalTaxCents > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">{taxLabel}</span>
                          <span className="text-xs text-slate-500">{formatCAD(taxBreakdown.totalTaxCents)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-0.5 border-t border-slate-100">
                        <span className="text-sm font-semibold text-slate-700">Total</span>
                        <span className="text-sm font-semibold text-slate-800">{formatCAD(grandTotalCents)}</span>
                      </div>
                    </div>
                  )}

                  {/* Preference memory hint */}
                  <p className="text-[11px] text-slate-400 italic flex items-center gap-1.5">
                    <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Z"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    {hasSavedPrefs
                      ? "Loaded your saved billing preferences for this program. Changes will be remembered."
                      : "Your billing preferences for this program will be remembered for next time."}
                  </p>

                  {/* ── Initial Invoice (toggle always visible without scrolling) ── */}
                  <div className="pt-1">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        Initial Invoice
                      </span>
                      <div className="flex-1 h-px bg-slate-100" />
                      <Toggle
                        checked={createInitialInvoice}
                        onChange={(val) => setCreateInitialInvoice(val)}
                        disabled={grandTotalCents === 0 && enabledDisbursements.length === 0}
                      />
                    </div>
                  </div>

                  {createInitialInvoice && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
                      {/* Running total with tax */}
                      <div className="space-y-1.5 text-sm">
                        {legalFeeCents > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">{legalFeeLabel}</span>
                            <span className="font-medium text-slate-700">{formatCAD(legalFeeCents)}</span>
                          </div>
                        )}
                        {enabledDisbursements.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">
                              Additional charges ({enabledDisbursements.length})
                            </span>
                            <span className="font-medium text-slate-700">
                              {formatCAD(totalDisbursementsCents)}
                            </span>
                          </div>
                        )}
                        {taxBreakdown.totalTaxCents > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">{taxLabel}</span>
                            <span className="font-medium text-emerald-700">{formatCAD(taxBreakdown.totalTaxCents)}</span>
                          </div>
                        )}
                        <div className="border-t border-slate-200 pt-1.5 flex justify-between">
                          <span className="font-semibold text-slate-700">Total</span>
                          <span className="font-semibold text-slate-900">{formatCAD(grandTotalCents)}</span>
                        </div>
                      </div>

                      {/* Invoice mode selector */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-500">How much to invoice?</p>
                        {[
                          { value: "full" as const, label: "Full amount", sub: formatCAD(grandTotalCents) },
                          ...(legalFeeCents > 0
                            ? [{ value: "partial" as const, label: "Partial / retainer", sub: null }]
                            : []),
                          ...(enabledDisbursements.length > 0
                            ? [
                                {
                                  value: "disbursements_only" as const,
                                  label: "Charges only",
                                  sub: formatCAD(totalDisbursementsCents),
                                },
                              ]
                            : []),
                        ].map((opt) => (
                          <label
                            key={opt.value}
                            className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                              invoiceMode === opt.value
                                ? "border-brand-light bg-brand-light"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <input
                              type="radio"
                              name="invoiceMode"
                              value={opt.value}
                              checked={invoiceMode === opt.value}
                              onChange={() => setInvoiceMode(opt.value)}
                              className="accent-brand-accent"
                            />
                            <span className="flex-1 text-sm text-slate-700">{opt.label}</span>
                            {opt.sub && (
                              <span className="text-xs font-medium text-slate-500">{opt.sub}</span>
                            )}
                          </label>
                        ))}

                        {invoiceMode === "partial" && (
                          <div className="relative ml-8">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                            <input
                              type="number"
                              value={partialAmountDollars}
                              onChange={(e) => setPartialAmountDollars(e.target.value)}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              className="w-full rounded-lg border border-slate-200 pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                            />
                            {partialCents > grandTotalCents && grandTotalCents > 0 && (
                              <p className="text-xs text-amber-600 mt-1">
                                Amount exceeds total ({formatCAD(grandTotalCents)})
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Due date options */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-500">Due date</p>
                        <div className="flex gap-2">
                          {[
                            { value: "1" as const, label: "1 Day" },
                            { value: "7" as const, label: "7 Days" },
                            { value: "30" as const, label: "30 Days" },
                            { value: "custom" as const, label: "Custom" },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setDueDateOption(opt.value)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                dueDateOption === opt.value
                                  ? "bg-brand-accent text-white"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        {dueDateOption === "custom" && (
                          <input
                            type="date"
                            value={customDueDate}
                            onChange={(e) => setCustomDueDate(e.target.value)}
                            min={new Date().toISOString().slice(0, 10)}
                            className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                          />
                        )}
                      </div>

                      <p className="text-xs text-slate-400">
                        A draft invoice for {formatCAD(invoiceAmountCents)}{taxBreakdown.totalTaxCents > 0 ? ` (incl. ${taxLabel})` : ""} will be created. Due{" "}
                        {formatDueDate()}.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* ═══════════════════════════════════════════════════════════════
                  Step 4: Review & Create
                  ═══════════════════════════════════════════════════════════════ */}
              {step === 4 && (
                <>
                  {/* ── Case Title ── */}
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Case Title
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="Case title"
                        className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                      />
                    </div>
                  </div>

                  {/* ── Section 1: Jurisdiction & Program ── */}
                  <div className="flex items-center gap-3 pt-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Jurisdiction & Program</span>
                    <div className="flex-1 h-px bg-slate-100" />
                    <button type="button" onClick={() => goToStep(1)} className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full ${COUNTRY_COLORS[form.country] || "bg-slate-400"} flex items-center justify-center`}>
                        <span className="text-xs font-bold text-white">{form.country}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{countryName}{form.region ? `  -  ${form.region}` : ""}</p>
                        <p className="text-xs text-slate-500">Jurisdiction</p>
                      </div>
                    </div>
                    <div className="border-t border-slate-100 pt-3 flex items-center gap-3">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{selectedProgram?.name || form.programKey}</p>
                        <p className="text-xs text-slate-500">Program</p>
                      </div>
                      {form.caseType && (
                        <span className="px-2.5 py-1 rounded-full bg-brand-light text-brand text-xs font-medium capitalize">
                          {CASE_TYPES.find((ct) => ct.value === form.caseType)?.label || form.caseType}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Section 2: Client & Team ── */}
                  <div className="flex items-center gap-3 pt-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Client & Team</span>
                    <div className="flex-1 h-px bg-slate-100" />
                    <button type="button" onClick={() => goToStep(2)} className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand-light text-brand flex items-center justify-center text-sm font-bold">
                        {selectedClient ? getUserInitials(selectedClient.displayName) : "?"}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{selectedClient?.displayName || "No client selected"}</p>
                        <p className="text-xs text-slate-500">Client</p>
                      </div>
                    </div>

                    {selectedUserIds.length > 0 && (
                      <div className="border-t border-slate-100 pt-3">
                        <p className="text-xs text-slate-500 mb-2">Team ({selectedUserIds.length})</p>
                        <div className="flex flex-wrap gap-3">
                          {selectedUserIds.map((uid, idx) => {
                            const u = users.find((usr: any) => usr._id === uid);
                            if (!u) return null;
                            return (
                              <div key={uid} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50">
                                <div className="w-7 h-7 rounded-full bg-brand-light text-brand flex items-center justify-center text-[10px] font-bold">
                                  {getUserInitials(u.name)}
                                </div>
                                <span className="text-xs font-medium text-slate-700">{u.name?.split(" ")[0]}</span>
                                {idx === 0 && (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-brand-accent text-white">Lead</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Section 3: Milestones (mini vertical timeline) ── */}
                  {milestones.length > 0 && (
                    <>
                      <div className="flex items-center gap-3 pt-3">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Milestones</span>
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-brand-light text-brand">{milestones.length}</span>
                        <div className="flex-1 h-px bg-slate-100" />
                        <button type="button" onClick={() => goToStep(1)} className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5">
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                      </div>

                      <div className="bg-white rounded-xl border border-slate-200 p-4 max-h-48 overflow-y-auto">
                        <div className="relative pl-6">
                          <div className="absolute left-[9px] top-1.5 bottom-1.5 w-px bg-slate-200" />
                          {milestones.map((m, idx) => (
                            <div key={m.key} className="relative flex items-center gap-3 py-1.5">
                              <div className="absolute left-[-15px] w-[7px] h-[7px] rounded-full bg-brand-accent border-2 border-white" />
                              <span className="text-sm text-slate-700">{m.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── Section 4: Billing & Invoice ── */}
                  <div className="flex items-center gap-3 pt-3">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Billing</span>
                    <div className="flex-1 h-px bg-slate-100" />
                    <button type="button" onClick={() => goToStep(3)} className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-slate-400" />
                      <span className="text-sm font-medium text-slate-700">
                        {FEE_TYPE_OPTIONS.find((o) => o.value === form.feeType)?.label || form.feeType}
                      </span>
                    </div>

                    {/* All charges as unified line items */}
                    {(legalFeeCents > 0 || enabledDisbursements.length > 0) && (
                      <div className="border-t border-slate-100 pt-3">
                        <div className="space-y-1">
                          {form.feeType !== "hourly" && form.flatFeeCents > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-600 truncate mr-2">{legalFeeLabel}</span>
                              <span className="text-slate-700 font-medium flex-shrink-0">{formatCAD(form.flatFeeCents)}</span>
                            </div>
                          )}
                          {enabledDisbursements.map((d) => (
                            <div key={d.id} className="flex items-center justify-between text-xs">
                              <span className="text-slate-600 truncate mr-2">{d.name}</span>
                              <span className="text-slate-700 font-medium flex-shrink-0">{formatCAD(d.amountCents)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {taxBreakdown.totalTaxCents > 0 && (
                      <div className="border-t border-slate-100 pt-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">{taxLabel}</span>
                          <span className="text-emerald-700 font-medium">{formatCAD(taxBreakdown.totalTaxCents)}</span>
                        </div>
                      </div>
                    )}

                    {grandTotalCents > 0 && (
                      <div className="border-t border-slate-200 pt-3 mt-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700">Total</span>
                          <span className="text-sm font-bold text-slate-900">{formatCAD(grandTotalCents)}</span>
                        </div>
                      </div>
                    )}

                    {createInitialInvoice && invoiceAmountCents > 0 && (
                      <div className="border-t border-slate-100 pt-3">
                        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3.5 py-2.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Receipt className="h-4 w-4 text-emerald-600" />
                              <span className="text-sm font-medium text-emerald-800">Initial Invoice</span>
                            </div>
                            <span className="text-sm font-bold text-emerald-800">{formatCAD(invoiceAmountCents)}</span>
                          </div>
                          <p className="text-xs text-emerald-600 mt-1">
                            {invoiceMode === "full" ? "Full amount" : invoiceMode === "partial" ? "Partial / retainer" : "Charges only"}  -  Due {formatDueDate()}
                          </p>
                          <label className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-emerald-200 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={sendInvoice}
                              onChange={(e) => setSendInvoice(e.target.checked)}
                              className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-xs font-medium text-emerald-700">Send invoice to client on creation</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {error && (
                <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </PremiumModal>

      <QuickCreateClientModal
        open={showQuickCreateClient}
        onClose={() => setShowQuickCreateClient(false)}
        onCreated={(newClient) => {
          setClients((prev: any[]) => [...prev, newClient]);
          setForm((prev) => ({ ...prev, clientId: newClient._id }));
          setShowQuickCreateClient(false);
        }}
      />

      {/* Quick Create Program Modal */}
      <PremiumModal
        open={showQuickCreateProgram}
        onClose={() => setShowQuickCreateProgram(false)}
        title="Create Custom Program"
        icon={Plus}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowQuickCreateProgram(false)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={quickProgramSaving || !quickProgram.name}
              onClick={async () => {
                setQuickProgramSaving(true);
                try {
                  const programKey = quickProgram.name
                    .toUpperCase()
                    .replace(/[^A-Z0-9]+/g, "_")
                    .replace(/(^_|_$)/g, "");
                  const res = await immigrationProgramsApi.create({
                    jurisdiction: { country: form.country },
                    programKey: `${form.country}_${programKey}`,
                    name: quickProgram.name,
                    category: quickProgram.category,
                    caseType: quickProgram.caseType,
                  });
                  setPrograms((prev) => [...prev, res.data]);
                  setForm((prev) => ({ ...prev, programKey: res.data.programKey }));
                  setShowQuickCreateProgram(false);
                  setQuickProgram({ name: "", category: "WORK", caseType: "temporary_resident" });
                } catch (err: any) {
                  alert(err.response?.data?.error || "Failed to create program");
                } finally {
                  setQuickProgramSaving(false);
                }
              }}
              className="px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {quickProgramSaving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </span>
              ) : (
                "Create"
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Program Name *</label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={quickProgram.name}
                onChange={(e) => setQuickProgram({ ...quickProgram, name: e.target.value })}
                className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                placeholder="e.g. Spousal Open Work Permit"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
            <SearchableSelect
              options={PROGRAM_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
              value={quickProgram.category}
              onChange={(val) => setQuickProgram({ ...quickProgram, category: val || "WORK" })}
              placeholder="Select category"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Case Type</label>
            <SearchableSelect
              options={CASE_TYPES.map((ct) => ({ value: ct.value, label: ct.label }))}
              value={quickProgram.caseType}
              onChange={(val) => setQuickProgram({ ...quickProgram, caseType: val || "temporary_resident" })}
              placeholder="Select case type"
            />
          </div>
        </div>
      </PremiumModal>
    </>
  );
};

export default CreateImmigrationCaseModal;
